import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  currentSession,
  fetchRoomBoard,
  moveRoom,
  setRoomService,
  type RoomBoardView,
} from '@hk/core'

/* The board's data lifecycle.

   REFRESH_MS is the promise the screen makes to a supervisor: what you are looking at
   is at most thirty seconds old. STALE_AFTER_MS is what happens when that promise is
   broken — after ninety seconds without a successful fetch the screen SAYS SO.

   A board that has quietly stopped updating is worse than one that is visibly down,
   because a supervisor will keep acting on it. Ninety seconds is three missed refreshes:
   long enough that one slow response does not cry wolf, short enough that nobody
   assigns a room from a board that stopped at seven. */

export const REFRESH_MS = 30_000
export const STALE_AFTER_MS = 90_000

export interface RoomBoardState {
  data: RoomBoardView | undefined
  error: unknown
  isLoading: boolean
  /** True while showing data older than STALE_AFTER_MS. */
  isStale: boolean
  /** When the shown data was fetched. Undefined before the first success. */
  updatedAt: Date | undefined
  refetch: () => void

  /* ── SPRINT 35: THE OVERRIDE ─────────────────────────────────────────────────
     T8 and only T8. See `moveRoom` in @hk/core for why the other eight transitions
     do not belong on a screen that moves a room and nothing else. */

  /** Record a dirty room as clean, with the reason the rule demands. Resolves true
   *  when the server took it — the dialog needs to know whether to close. */
  markClean: (roomId: string, reason: string) => Promise<boolean>
  markingClean: boolean
  markCleanError: unknown
  /** Forget a refusal, so reopening the dialog does not show last time's. */
  clearMarkCleanError: () => void

  /* ── CQ-138: TAKING A ROOM OFF SALE BY HAND ──────────────────────────────────
     A different act from the override and so a different mutation, not a flag on it:
     one moves the room through its cleaning states, the other decides whether the hotel
     may sell it at all. Sharing a `pending` between them would grey out the wrong button. */

  /** Withdraw the room from sale, or put it back, with the reason the rule demands. */
  setService: (roomId: string, outOfService: boolean, reason: string) => Promise<boolean>
  settingService: boolean
  serviceError: unknown
  clearServiceError: () => void
}

export function useRoomBoard(propertyId: string): RoomBoardState {
  const query = useQuery({
    queryKey: ['room-board', propertyId],
    queryFn: ({ signal }) => fetchRoomBoard(propertyId, { signal }),
    refetchInterval: REFRESH_MS,
    // Keep polling with the tab in the background: a supervisor leaves the board open
    // on a second screen all morning and glances at it.
    refetchIntervalInBackground: true,
    // Show the previous board while the next one loads, rather than flashing a
    // skeleton over a screen somebody is reading.
    placeholderData: (previous) => previous,
  })

  const isStale = useElapsedSince(query.dataUpdatedAt, STALE_AFTER_MS)

  const client = useQueryClient()

  const override = useMutation({
    mutationFn: ({ roomId, reason }: { roomId: string; reason: string }) =>
      moveRoom(roomId, {
        toState: 'CLEAN',
        actorUserId: currentSession().userId,
        reason,
      }),
    /* NOT OPTIMISTIC, and for the same reason the assignment board's move is not: a tile
       that turns green and quietly turns back is worse than one that takes a moment,
       because the supervisor has already moved on and the room is still dirty. The board
       re-reads and the answer comes from the server. */
    onSettled: (_data, _error, variables) => {
      void client.invalidateQueries({ queryKey: ['room-board', propertyId] })

      /* **AND THE ROOM'S HISTORY IS REMOVED, NOT INVALIDATED. DEFECT #123.**

         The override succeeded, the tile turned green, and reopening the panel said
         *"Nothing has happened to this room in the last 7 days"* — the one panel in the
         product whose job is to record what happened, denying the very act it had just
         recorded. Found by rendering it against a real server: the API had the row and the
         screen did not.

         **`invalidateQueries` IS NOT ENOUGH, AND THAT IS THE WHOLE OF THIS COMMENT.** It
         marks the entry stale and hands over the cached answer while it refetches behind
         it — so the panel draws the old answer first and corrects itself a moment later.
         For most screens that is the right trade and it is why the board does exactly that.

         **AND SPRINT 62 MADE IT WORSE RATHER THAN BETTER.** W-21 keeps the history panel
         on screen beside the act, so the query is now ACTIVE when the override lands: an
         invalidate would repaint "nothing has happened to this room" and then correct
         itself, in front of the person who just performed the act, instead of doing it on
         a reopen where nobody was looking. `removeQueries` is the right call either way,
         and the reason it is right got stronger.

         **HERE THE CACHED ANSWER IS A FALSE STATEMENT.** "Nothing has happened to this
         room" is not stale data, it is a denial, and a panel that denies an act for half a
         second in front of the person who performed it has spent the credibility it exists
         for (#118's lesson, on a different screen). `removeQueries` drops the entry, so the
         next open has nothing to show and says it is reading — *a spinner is a true
         sentence and a stale one is not.* */
      client.removeQueries({ queryKey: ['room-history', variables.roomId] })
    },
  })

  /* **CQ-138 — OFF SALE AND BACK.** Not optimistic, for the reason the override is not:
     a tile that greys out and quietly comes back is worse than one that takes a moment.
     The room's HISTORY is not touched here — this act does not write a room status row,
     it writes an activity entry, and W-124 is a different screen with its own query. */
  const service = useMutation({
    mutationFn: ({ roomId, outOfService, reason }:
      { roomId: string; outOfService: boolean; reason: string }) =>
      setRoomService(roomId, outOfService, reason),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['room-board', propertyId] })
    },
  })

  return {
    data: query.data,
    error: query.error,
    isLoading: query.isLoading,
    isStale: query.dataUpdatedAt > 0 && isStale,
    updatedAt: query.dataUpdatedAt > 0 ? new Date(query.dataUpdatedAt) : undefined,
    refetch: () => void query.refetch(),

    /* `mutateAsync` REJECTS on a refusal, and an unhandled rejection in a click handler is
       a console error in development and nothing at all in production. Caught here, once,
       at the boundary — the error is already on its way to the dialog through
       `markCleanError`, and the boolean says only whether to close. */
    markClean: (roomId, reason) =>
      override.mutateAsync({ roomId, reason }).then(() => true, () => false),
    markingClean: override.isPending,
    markCleanError: override.error,
    clearMarkCleanError: () => override.reset(),

    setService: (roomId, outOfService, reason) =>
      service.mutateAsync({ roomId, outOfService, reason }).then(() => true, () => false),
    settingService: service.isPending,
    serviceError: service.error,
    clearServiceError: () => service.reset(),
  }
}

/**
 * Whether `threshold` has passed since `timestamp`.
 *
 * This is a hook and not a comparison at render time for one reason: nothing re-renders
 * a board that has stopped receiving data. Without its own timer the stale banner would
 * appear at the next successful fetch — which is precisely the moment it is no longer
 * true.
 */
function useElapsedSince(timestamp: number, threshold: number): boolean {
  const [elapsed, setElapsed] = useState(false)

  useEffect(() => {
    if (!timestamp) return
    setElapsed(Date.now() - timestamp >= threshold)

    const tick = setInterval(
      () => setElapsed(Date.now() - timestamp >= threshold),
      Math.max(1000, Math.floor(threshold / 6)))
    return () => clearInterval(tick)
  }, [timestamp, threshold])

  return elapsed
}
